import type { Batch } from "@/lib/types";
import { useTranslations } from "@/i18n/LocaleProvider";
import { SHOW_PRICING } from "@/lib/featureFlags";
import Spinner from "@/components/Spinner";

export default function BatchListStep({
  batches,
  loading,
  onSelect,
}: {
  batches: Batch[];
  loading: boolean;
  onSelect: (batch: Batch) => void;
}) {
  const { t } = useTranslations();
  return (
    <div className="mt-3 space-y-2 sm:mt-4 sm:space-y-2.5">
      <h1 className="text-center font-display text-xl sm:text-2xl">{t("batches.list.title")}</h1>
      <p className="text-center text-sm text-muted">{t("batches.list.subtitle")}</p>
      {loading ? (
        <div className="flex justify-center py-6 text-muted sm:py-8">
          <Spinner className="size-6" />
        </div>
      ) : (
        batches.length === 0 && (
          <p className="text-sm text-muted">{t("batches.list.empty")}</p>
        )
      )}
      {batches.map((batch) => {
        const seatsLeft = batch.capacity - batch.seatsTaken;
        const isFull = batch.status === "full" || seatsLeft <= 0;
        const durationWeeks = Math.round((batch.endsOn - batch.startsOn) / (7 * 24 * 60 * 60 * 1000));
        return (
          <button
            key={batch.id}
            onClick={() => !isFull && onSelect(batch)}
            disabled={isFull}
            className="flex w-full flex-col gap-1 rounded-xl border border-foreground/10 bg-surface px-4 py-2.5 text-left transition hover:border-accent disabled:cursor-not-allowed disabled:opacity-50 sm:py-3"
          >
            <div className="flex items-center justify-between gap-4">
              <span className="font-semibold">{batch.name}</span>
              {SHOW_PRICING && <span className="font-semibold text-accent">₹{batch.priceInr}</span>}
            </div>
            <p className="text-sm text-muted">
              {t("batches.list.starts", {
                date: new Intl.DateTimeFormat("en-IN", {
                  dateStyle: "medium",
                  timeZone: "Asia/Kolkata",
                }).format(new Date(batch.startsOn)),
              })}
              {" · "}
              {t("batches.list.duration", { weeks: durationWeeks })}
            </p>
            <p className="text-sm text-muted">
              {isFull ? t("batches.list.full") : t("batches.list.seatsLeft", { seats: seatsLeft })}
            </p>
          </button>
        );
      })}
    </div>
  );
}
